// Търсене, толерантно към „шльокавица" (български думи, изписани на латиница)
// и към произволни съкращения по думи.
//
// Как работи: и текстът, и заявката се свеждат до ОБЩА канонична азбука, в
// която многобуквените звуци са ЕДИН символ (ж → Z, ц → C, ч → Q, ш → S,
// щ → W, ю → U, я → A, ъ → Y). Така „минерална вода" става „minerYlna voda"
// независимо дали е писано на кирилица, или на латиница.
//
// Заявката се превежда до РЕГУЛЯРЕН ИЗРАЗ, а не до низ, защото част от
// латинските букви са двусмислени: „y" може да е й/ъ/ю, „c" може да е ц/к.
// Всяка такава буква става клас от възможности. Цифрите НЕ са шльокавица
// (4≠ч, 6≠ш, 9≠я) — четат се буквално, защото цифровата заявка е PLU код
// или цифра от името („Пица 4 сирена").
//
// Заявката е многодумна — всяка дума (по интервали) трябва да се среща някъде
// в текста. Затова „минерална вода" излиза и на „мин", и на „м в", и на „min",
// и на „m v".

/** Диапазон в ОРИГИНАЛНИЯ текст, покрит от съвпадение (за подчертаване). */
export interface SearchRange {
  start: number;
  end: number;
}

// Кирилица → канонична азбука. „ь" няма собствен звук → изпада.
const CYRILLIC_TO_CANONICAL: Record<string, string> = {
  а: "a",
  б: "b",
  в: "v",
  г: "g",
  д: "d",
  е: "e",
  ж: "Z",
  з: "z",
  и: "i",
  й: "i",
  к: "k",
  л: "l",
  м: "m",
  н: "n",
  о: "o",
  п: "p",
  р: "r",
  с: "s",
  т: "t",
  у: "u",
  ф: "f",
  х: "h",
  ц: "C",
  ч: "Q",
  ш: "S",
  // Собствен символ, не „St" (ш+т): иначе „ш" в заявката намира и „щ"
  щ: "W",
  ъ: "Y",
  ь: "",
  ю: "U",
  я: "A",
};

// Латиницата В ТЕКСТА се свежда до същата азбука (имена на английски или
// продукти, изписани на латиница) — иначе двете страни не биха се срещнали.
const TEXT_LATIN_SEQUENCES: [string, string][] = [
  ["sht", "W"],
  ["sh", "S"],
  ["ch", "Q"],
  ["zh", "Z"],
  ["ts", "C"],
  ["yu", "U"],
  ["ya", "A"],
];

const TEXT_LATIN_LETTERS: Record<string, string> = {
  j: "Z",
  y: "i",
  c: "C",
  x: "h",
  w: "v",
  q: "A",
};

// Латиницата В ЗАЯВКАТА → парче регулярен израз. Двойките се проверяват преди
// единичните букви, а фалбекът след „|" пази буквалния прочит („сх" срещу „ш").
const QUERY_LATIN_SEQUENCES: [string, string][] = [
  ["sht", "(?:W|St|sht)"], // щ или ш+т
  ["sh", "(?:S|W|sh)"], // „sh" е и началото на „sht" (щ), докато се пише
  ["ch", "(?:Q|ch)"],
  ["zh", "(?:Z|zh)"],
  ["ts", "(?:C|ts)"],
  ["yu", "(?:U|iu)"],
  ["iu", "(?:U|iu)"],
  ["ju", "(?:U|Zu)"],
  ["ya", "(?:A|ia)"],
  ["ia", "(?:A|ia)"],
  ["ja", "(?:A|Za)"],
];

const QUERY_LATIN_LETTERS: Record<string, string> = {
  a: "[aY]", // а или ъ („kurpa"/„karpa")
  u: "[uUY]", // у, ю или ъ
  y: "[iYU]", // й, ъ или ю
  j: "[Zi]", // ж или й
  c: "[Ck]", // ц или к („cola")
  w: "[SWv]", // ш, щ или в
  x: "h",
  q: "A",
  // Клавишите на ч ш щ от българската фонетична подредба, натиснати на
  // английска — писалият е мислил на кирилица, търсим само буквата
  "`": "Q", // ч
  "[": "S", // ш
  "]": "W", // щ
};

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

interface CanonicalText {
  canonical: string;
  /** За всеки символ от `canonical` — началото му в оригиналния текст. */
  sourceStart: number[];
  /** За всеки символ от `canonical` — краят му (exclusive) в оригинала. */
  sourceEnd: number[];
}

/** Свежда текст до каноничната азбука, пазейки връзката към оригинала. */
function toCanonical(text: string): CanonicalText {
  const lower = text.toLowerCase();
  let canonical = "";
  const sourceStart: number[] = [];
  const sourceEnd: number[] = [];
  let index = 0;

  const push = (mapped: string, from: number, to: number) => {
    for (const symbol of mapped) {
      canonical += symbol;
      sourceStart.push(from);
      sourceEnd.push(to);
    }
  };

  while (index < lower.length) {
    const cyrillic = CYRILLIC_TO_CANONICAL[lower[index]];
    if (cyrillic !== undefined) {
      push(cyrillic, index, index + 1);
      index += 1;
      continue;
    }
    const sequence = TEXT_LATIN_SEQUENCES.find(([source]) =>
      lower.startsWith(source, index),
    );
    if (sequence) {
      push(sequence[1], index, index + sequence[0].length);
      index += sequence[0].length;
      continue;
    }
    push(
      TEXT_LATIN_LETTERS[lower[index]] ?? lower[index],
      index,
      index + 1,
    );
    index += 1;
  }

  return { canonical, sourceStart, sourceEnd };
}

/** Превежда една дума от заявката до регулярен израз над каноничната азбука. */
function toPattern(token: string): string {
  const lower = token.toLowerCase();
  let pattern = "";
  let index = 0;

  while (index < lower.length) {
    const cyrillic = CYRILLIC_TO_CANONICAL[lower[index]];
    if (cyrillic !== undefined) {
      pattern += escapeRegExp(cyrillic);
      index += 1;
      continue;
    }
    const sequence = QUERY_LATIN_SEQUENCES.find(([source]) =>
      lower.startsWith(source, index),
    );
    if (sequence) {
      pattern += sequence[1];
      index += sequence[0].length;
      continue;
    }
    pattern +=
      QUERY_LATIN_LETTERS[lower[index]] ?? escapeRegExp(lower[index]);
    index += 1;
  }

  return pattern;
}

export interface SearchMatcher {
  /** Празна заявка — всичко минава, нищо не се подчертава. */
  isEmpty: boolean;
  matches(text: string): boolean;
  ranges(text: string): SearchRange[];
}

// Кеш на компилираните заявки — highlight-ът вика matcher-а на ВСЕКИ ред, а
// компилирането на регулярен израз е скъпо. Матчърите са без състояние
// (`lastIndex` се нулира преди всяко ползване), затова са безопасни за споделяне.
const MATCHER_CACHE_LIMIT = 20;
const matcherCache = new Map<string, SearchMatcher>();

/**
 * Компилира заявката ВЕДНЪЖ (регулярните изрази са скъпи) и се преизползва за
 * всички редове на списъка.
 */
export function createSearchMatcher(term: string): SearchMatcher {
  const cached = matcherCache.get(term);
  if (cached) return cached;
  const matcher = compileSearchMatcher(term);
  if (matcherCache.size >= MATCHER_CACHE_LIMIT) {
    matcherCache.delete(matcherCache.keys().next().value as string);
  }
  matcherCache.set(term, matcher);
  return matcher;
}

function compileSearchMatcher(term: string): SearchMatcher {
  const tokens = term.trim().split(/\s+/).filter(Boolean);
  // Регистърът НЕ се игнорира — каноничните символи (S = ш) се различават от
  // обикновените букви (s = с) само по него.
  const patterns = tokens
    .map(toPattern)
    .filter(Boolean)
    .map((pattern) => new RegExp(pattern, "g"));

  if (patterns.length === 0) {
    return {
      isEmpty: true,
      matches: () => true,
      ranges: () => [],
    };
  }

  return {
    isEmpty: false,
    matches(text: string) {
      if (!text) return false;
      const { canonical } = toCanonical(text);
      return patterns.every((pattern) => {
        pattern.lastIndex = 0;
        return pattern.test(canonical);
      });
    },
    ranges(text: string) {
      if (!text) return [];
      const { canonical, sourceStart, sourceEnd } = toCanonical(text);
      const found: SearchRange[] = [];
      for (const pattern of patterns) {
        pattern.lastIndex = 0;
        let match = pattern.exec(canonical);
        while (match) {
          if (match[0].length > 0) {
            found.push({
              start: sourceStart[match.index],
              end: sourceEnd[match.index + match[0].length - 1],
            });
          } else {
            pattern.lastIndex += 1;
          }
          match = pattern.exec(canonical);
        }
      }
      return mergeRanges(found);
    },
  };
}

/** Еднократна проверка — за списъци ползвай `createSearchMatcher`. */
export function matchesSearch(text: string, term: string): boolean {
  return createSearchMatcher(term).matches(text);
}

/**
 * Съвпадение по PLU код — ОТДЕЛНО от буквения matcher, който гледа имената.
 * Заявка само от цифри съвпада, когато е префикс на кода („12" намира PLU 123).
 */
export function matchesPluCode(
  pluCode: number | null | undefined,
  term: string,
): boolean {
  const digits = term.trim();
  if (!/^\d+$/.test(digits)) return false;
  return pluCode != null && String(pluCode).startsWith(digits);
}

/** Слива застъпващите се диапазони, за да не се рендират един върху друг. */
function mergeRanges(ranges: SearchRange[]): SearchRange[] {
  if (ranges.length < 2) return ranges;
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: SearchRange[] = [sorted[0]];
  for (const range of sorted.slice(1)) {
    const last = merged[merged.length - 1];
    if (range.start <= last.end) {
      last.end = Math.max(last.end, range.end);
    } else {
      merged.push({ ...range });
    }
  }
  return merged;
}
